// Shared display formatters — the single source of truth so the same figure
// renders identically on every screen.
//
// Locale is pinned to "en-GB" on purpose: several call sites previously passed
// `undefined`, which meant currency/number output varied with the *viewer's*
// browser locale (e.g. "1,234.50" vs "1.234,50"). Pinning removes that drift.
//
// Missing values render as an em dash ("—") rather than "£0.00" or "-", so a
// blank is never mistaken for a real zero — important on a tool people chase
// money over.

const GB = "en-GB";

/** £ with fixed decimals. `dp` overrides the 2dp default; `dash` the empty marker. */
export const formatCurrency = (value, { dp = 2, dash = "—" } = {}) => {
  if (value === null || value === undefined || Number.isNaN(Number(value))) return dash;
  return `£${Number(value).toLocaleString(GB, { minimumFractionDigits: dp, maximumFractionDigits: dp })}`;
};

/** Thousands-separated integer/number (no currency symbol). */
export const formatNumber = (value, { dp, dash = "—" } = {}) => {
  if (value === null || value === undefined || Number.isNaN(Number(value))) return dash;
  const opts = dp === undefined ? {} : { minimumFractionDigits: dp, maximumFractionDigits: dp };
  return Number(value).toLocaleString(GB, opts);
};

/** Percentage. `sign: true` prefixes a "+" on positives (for variance/deltas). */
export const formatPercent = (value, { dp = 1, dash = "—", sign = false } = {}) => {
  if (value === null || value === undefined || Number.isNaN(Number(value))) return dash;
  const n = Number(value);
  const prefix = sign && n > 0 ? "+" : "";
  return `${prefix}${n.toLocaleString(GB, { minimumFractionDigits: dp, maximumFractionDigits: dp })}%`;
};

/** Unit price: 4 dp for sub-£1 values (sub-penny precision), else 2 dp. */
export const formatUnitPrice = (value, { dash = "—" } = {}) => {
  if (value === null || value === undefined || Number.isNaN(Number(value))) return dash;
  const n = Number(value);
  const dp = Math.abs(n) < 1 ? 4 : 2;
  return `£${n.toLocaleString(GB, { minimumFractionDigits: dp, maximumFractionDigits: dp })}`;
};

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/** "2025-06" -> "Jun-25". Falls back to the raw string if unparseable. */
export const formatMonthShort = (ym) => {
  if (!ym || typeof ym !== "string" || ym.length < 7) return ym || "";
  const idx = parseInt(ym.slice(5, 7), 10) - 1;
  return idx >= 0 && idx <= 11 ? `${MONTHS[idx]}-${ym.slice(2, 4)}` : ym;
};

const SYMBOLS = {
  GBP: "£", EUR: "€", USD: "$", AUD: "A$",
  CAD: "C$", NZD: "NZ$", CHF: "CHF ", JPY: "¥",
};

/** Multi-currency money (symbol + locale-appropriate dp). JPY has 0 dp. */
export const formatMoney = (value, currency = "GBP", { dash = "—" } = {}) => {
  if (value === null || value === undefined || Number.isNaN(Number(value))) return dash;
  const sym = SYMBOLS[currency] || "";
  const dp = currency === "JPY" ? 0 : 2;
  return `${sym}${Number(value).toLocaleString(GB, { minimumFractionDigits: dp, maximumFractionDigits: dp })}`;
};
